import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Container,
  Stack,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Tabs,
  Tab,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  Rule as RuleIcon,
  PersonSearch as PersonSearchIcon,
  Report as ReportIcon,
  VpnKey as VpnKeyIcon,
  AccountTree as AccountTreeIcon,
  Category as CategoryIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Settings as FlowIcon,
  Group as GroupIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const EmailFlowPage = () => {
  const theme = useTheme();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Email template state
  const [template, setTemplate] = useState('');
  const [loadingTemplate, setLoadingTemplate] = useState(true);

  // Workflow management state
  const [workflows, setWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(true);
  const [availableDefectTypes, setAvailableDefectTypes] = useState([]);
  const [toGroups, setToGroups] = useState([]);
  const [ccGroups, setCcGroups] = useState([]);

  // Email logs state
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsPage, setLogsPage] = useState(0);
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [logsStatusFilter, setLogsStatusFilter] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  // Log detail dialog state
  const [logDetailOpen, setLogDetailOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    defect_type_id: '',
    to_group_id: '',
    cc_group_ids: [],
    min_height_cm: '',
    min_width_cm: ''
  });

  // UI state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    fetchEmailTemplate();
    fetchWorkflows();
    fetchAvailableDefectTypes();
    fetchGroups();
  }, []);

  // Fetch email logs when tab changes or pagination changes
  useEffect(() => {
    if (activeTab === 1) {
      fetchEmailLogs();
    }
  }, [activeTab, logsPage, logsRowsPerPage, logsStatusFilter]);

  const fetchEmailTemplate = async () => {
      try {
        const res = await api.get('/emails/template');
        setTemplate(res.data);
      } catch (err) {
        console.error('Failed to fetch email template', err);
        setTemplate('<p>Failed to load email preview.</p>');
      } finally {
        setLoadingTemplate(false);
      }
    };

  const fetchWorkflows = async () => {
    try {
      setLoadingWorkflows(true);
      const res = await api.get('/email-workflows');
      setWorkflows(res.data.workflows || []);
    } catch (err) {
      console.error('Failed to fetch workflows', err);
      showSnackbar('Failed to fetch workflows', 'error');
    } finally {
      setLoadingWorkflows(false);
    }
  };

  const fetchAvailableDefectTypes = async () => {
    try {
      const res = await api.get('/email-workflows/available-defect-types');
      setAvailableDefectTypes(res.data.defectTypes || []);
    } catch (err) {
      console.error('Failed to fetch defect types', err);
    }
  };

  const fetchGroups = async () => {
    try {
      const [toRes, ccRes] = await Promise.all([
        api.get('/email-workflows/groups?email_role=TO'),
        api.get('/email-workflows/groups?email_role=CC')
      ]);
      setToGroups(toRes.data.groups || []);
      setCcGroups(ccRes.data.groups || []);
    } catch (err) {
      console.error('Failed to fetch groups', err);
    }
  };

  const fetchEmailLogs = async () => {
    try {
      setLoadingLogs(true);
      const params = {
        page: logsPage + 1,
        limit: logsRowsPerPage,
      };
      if (logsStatusFilter) {
        params.status = logsStatusFilter;
      }
      const res = await api.get('/email-workflows/logs', { params });
      setEmailLogs(res.data.logs || []);
      setLogsTotalCount(res.data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to fetch email logs', err);
      showSnackbar('Failed to fetch email logs', 'error');
    } finally {
      setLoadingLogs(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleLogsPageChange = (event, newPage) => {
    setLogsPage(newPage);
  };

  const handleLogsRowsPerPageChange = (event) => {
    setLogsRowsPerPage(parseInt(event.target.value, 10));
    setLogsPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setLogsStatusFilter(event.target.value);
    setLogsPage(0);
  };

  const openLogDetail = (log) => {
    setSelectedLog(log);
    setLogDetailOpen(true);
  };

  const closeLogDetail = () => {
    setLogDetailOpen(false);
    setSelectedLog(null);
  };

  const openAddDialog = () => {
    setEditingWorkflow(null);
    setFormData({
      name: '',
      defect_type_id: '',
      to_group_id: '',
      cc_group_ids: [],
      min_height_cm: '',
      min_width_cm: ''
    });
    setActiveStep(0);
    setDialogOpen(true);
  };

  const openEditDialog = (workflow) => {
    setEditingWorkflow(workflow);
    setFormData({
      name: workflow.workflow_name,
      defect_type_id: workflow.defect_type_id,
      to_group_id: workflow.to_group_id,
      cc_group_ids: workflow.cc_groups ? workflow.cc_groups.map(g => g.id) : [],
      min_height_cm: workflow.min_height_cm || '',
      min_width_cm: workflow.min_width_cm || ''
    });
    setActiveStep(0);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingWorkflow(null);
    setActiveStep(0);
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const payload = {
        ...formData,
        min_height_cm: formData.min_height_cm ? parseFloat(formData.min_height_cm) : null,
        min_width_cm: formData.min_width_cm ? parseFloat(formData.min_width_cm) : null,
      };

      if (editingWorkflow) {
        // Update existing workflow
        await api.put(`/email-workflows/${editingWorkflow.id}`, payload);
        showSnackbar('Workflow updated successfully');
      } else {
        // Create new workflow
        await api.post('/email-workflows', payload);
        showSnackbar('Workflow created successfully');
      }

      handleCloseDialog();
      fetchWorkflows();
      fetchAvailableDefectTypes(); // Refresh available types
    } catch (err) {
      console.error('Failed to save workflow', err);
      showSnackbar(err.response?.data?.message || 'Failed to save workflow', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (workflowId) => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/email-workflows/${workflowId}`);
      showSnackbar('Workflow deleted successfully');
      fetchWorkflows();
      fetchAvailableDefectTypes(); // Refresh available types
    } catch (err) {
      console.error('Failed to delete workflow', err);
      showSnackbar(err.response?.data?.message || 'Failed to delete workflow', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Workflow Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Pothole Response Team"
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel>Defect Type</InputLabel>
              <Select
                value={formData.defect_type_id}
                onChange={(e) => setFormData({ ...formData, defect_type_id: e.target.value })}
                disabled={editingWorkflow} // Lock defect type when editing
              >
                {editingWorkflow ? (
                  <MenuItem value={editingWorkflow.defect_type_id}>
                    {editingWorkflow.defect_type_name}
                  </MenuItem>
                ) : (
                  availableDefectTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
              Dimension Threshold (optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Only defects exceeding these dimensions will be included in the email. Leave empty to include all.
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                type="number"
                label="Min Height (cm)"
                value={formData.min_height_cm}
                onChange={(e) => setFormData({ ...formData, min_height_cm: e.target.value })}
                placeholder="e.g., 20"
                inputProps={{ min: 0, step: 0.1 }}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                type="number"
                label="Min Width (cm)"
                value={formData.min_width_cm}
                onChange={(e) => setFormData({ ...formData, min_width_cm: e.target.value })}
                placeholder="e.g., 30"
                inputProps={{ min: 0, step: 0.1 }}
                size="small"
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the primary recipient group (TO) for this defect type:
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Primary Recipient Group (TO)</InputLabel>
              <Select
                value={formData.to_group_id}
                onChange={(e) => setFormData({ ...formData, to_group_id: e.target.value })}
              >
                {toGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name} ({group.user_count} users)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select CC recipient groups (optional). These users will receive the email for awareness:
            </Typography>
            <Autocomplete
              multiple
              options={ccGroups}
              getOptionLabel={(option) => `${option.name} (${option.user_count} users)`}
              value={ccGroups.filter(group => formData.cc_group_ids.includes(group.id))}
              onChange={(event, newValue) => {
                setFormData({ ...formData, cc_group_ids: newValue.map(g => g.id) });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="CC Recipient Groups"
                  placeholder="Select groups for CC"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  const isStepValid = (step) => {
    switch (step) {
      case 0:
        return formData.name && formData.defect_type_id;
      case 1:
        return formData.to_group_id;
      case 2:
        return true; // CC groups are optional
      default:
        return false;
    }
  };

  const steps = [
    { label: 'Workflow Details', description: 'Name and defect type' },
    { label: 'Primary Recipients', description: 'Select TO group' },
    { label: 'CC Recipients', description: 'Select CC groups (optional)' }
  ];

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-MY', {
      timeZone: 'Asia/Kuala_Lumpur',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render Workflows Tab Content
  const renderWorkflowsTab = () => (
    <>
      {/* Email Workflows Section */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FlowIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Email Workflows
            </Typography>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
            disabled={availableDefectTypes.length === 0}
            size="small"
          >
            Add Flow
          </Button>
        </Stack>

        {loadingWorkflows ? (
          <CircularProgress />
        ) : workflows.length === 0 ? (
          <Alert severity="info">
            No email workflows configured yet. Click "Add Flow" to create your first workflow.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Workflow Name</TableCell>
                  <TableCell>Defect Type</TableCell>
                  <TableCell>Min Size (H x W)</TableCell>
                  <TableCell>Primary Group (TO)</TableCell>
                  <TableCell>CC Groups</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workflows.map((workflow) => (
                  <TableRow key={workflow.id}>
                    <TableCell>{workflow.workflow_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={workflow.defect_type_name}
                        size="small"
                        color={workflow.is_trafficsafety_related ? "warning" : "primary"}
                      />
                    </TableCell>
                    <TableCell>
                      {workflow.min_height_cm || workflow.min_width_cm ? (
                        <Typography variant="body2">
                          {workflow.min_height_cm ? `H\u2265${workflow.min_height_cm}cm` : '-'}
                          {' x '}
                          {workflow.min_width_cm ? `W\u2265${workflow.min_width_cm}cm` : '-'}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">All sizes</Typography>
                      )}
                    </TableCell>
                    <TableCell>{workflow.to_group_name}</TableCell>
                    <TableCell>
                      {workflow.cc_groups && workflow.cc_groups.length > 0 ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {workflow.cc_groups.map((group) => (
                            <Chip
                              key={group.id}
                              label={group.name}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{workflow.created_by_name}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(workflow)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(workflow.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </>
  );

  // Render Email Logs Tab Content
  const renderEmailLogsTab = () => (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <HistoryIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Email Delivery Logs
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={logsStatusFilter}
              label="Status"
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={fetchEmailLogs} color="primary">
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {loadingLogs ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : emailLogs.length === 0 ? (
        <Alert severity="info">
          No email logs found. Logs will appear here after the daily email summary is sent.
        </Alert>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sent At</TableCell>
                  <TableCell>Workflow</TableCell>
                  <TableCell>Defect Type</TableCell>
                  <TableCell>TO</TableCell>
                  <TableCell>CC</TableCell>
                  <TableCell>Defects</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emailLogs.map((log) => {
                  const toRecipients = log.to_recipients || [];
                  const ccRecipients = log.cc_recipients || [];
                  const defectIds = log.defect_ids || [];

                  return (
                    <TableRow key={log.id}>
                      <TableCell>{formatDateTime(log.sent_at)}</TableCell>
                      <TableCell>{log.workflow_name || '-'}</TableCell>
                      <TableCell>
                        {log.defect_type_name ? (
                          <Chip label={log.defect_type_name} size="small" color="primary" />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={toRecipients.map(r => r.email || r.full_name).join(', ')}>
                          <Chip
                            label={`${toRecipients.length} recipient${toRecipients.length !== 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={ccRecipients.length > 0 ? ccRecipients.map(r => r.email || r.full_name).join(', ') : 'None'}>
                          <Chip
                            label={ccRecipients.length > 0 ? `${ccRecipients.length} recipient${ccRecipients.length !== 1 ? 's' : ''}` : 'None'}
                            size="small"
                            variant="outlined"
                            color={ccRecipients.length > 0 ? 'default' : 'default'}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={defectIds.length > 0 ? `IDs: ${defectIds.join(', ')}` : 'None'}>
                          <Chip
                            label={log.defect_count || 0}
                            size="small"
                            color="secondary"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={log.status === 'sent' ? <CheckCircleIcon /> : <ErrorIcon />}
                          label={log.status === 'sent' ? 'Sent' : 'Failed'}
                          size="small"
                          color={log.status === 'sent' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => openLogDetail(log)}>
                          <ReportIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={logsTotalCount}
            page={logsPage}
            onPageChange={handleLogsPageChange}
            rowsPerPage={logsRowsPerPage}
            onRowsPerPageChange={handleLogsRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}
    </Paper>
  );

  // Render Configuration Tab Content
  const renderConfigurationTab = () => (
    <>
      {/* Flow Overview */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <ScheduleIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Scheduled Task
          </Typography>
        </Stack>
        <Typography variant="body1" color="text.secondary" paragraph>
          The system is configured to automatically generate and send the daily defect summary email at a specific time.
        </Typography>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h4" fontWeight={700} color="primary.main" textAlign="center">
              7:30 PM
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Malaysia Time (UTC+8)
            </Typography>
          </CardContent>
        </Card>
      </Paper>

      {/* Summary cards - 2 cards in a single row using CSS Grid like AssetsPage */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)'
          },
          gap: 3,
          mb: 3
        }}
      >
        {/* Defect Selection Criteria */}
        <Card
          sx={{
            boxShadow: theme.shadows[2],
            borderRadius: '12px',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <ReportIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Defect Selection Criteria
              </Typography>
            </Stack>
            <Typography variant="body1" color="text.secondary" paragraph>
              The email summary only includes defects that meet all the following conditions:
            </Typography>
            <List dense sx={{ flexGrow: 1 }}>
              <ListItem>
                <ListItemIcon>
                  <VpnKeyIcon />
                </ListItemIcon>
                <ListItemText primary="Organization" secondary="PLUS Highways (ID: 11)" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AccountTreeIcon />
                </ListItemIcon>
                <ListItemText primary="Asset Type" secondary="Chainage Segment" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CategoryIcon />
                </ListItemIcon>
                <ListItemText primary="Defect Types" secondary={
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip label="Pothole" size="small" />
                    <Chip label="Foreign Object" size="small" />
                    <Chip label="Stray Animal" size="small" />
                  </Stack>
                } />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Recipient Rules */}
        <Card
          sx={{
            boxShadow: theme.shadows[2],
            borderRadius: '12px',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <PersonSearchIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Recipient Rules
              </Typography>
            </Stack>
            <Typography variant="body1" color="text.secondary" paragraph>
              A user will receive the email summary if they meet all the following criteria for a given defect:
            </Typography>
            <List dense sx={{ flexGrow: 1 }}>
              <ListItem>
                <ListItemIcon>
                  <RuleIcon />
                </ListItemIcon>
                <ListItemText primary="Active User" secondary="Must be an active user in the PLUS Highways organization." />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <RuleIcon />
                </ListItemIcon>
                <ListItemText primary="Location Access" secondary="Must have permission for the defect's network/section or organization-wide access." />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <RuleIcon />
                </ListItemIcon>
                <ListItemText primary="Discipline Subscription" secondary="Must be subscribed to the 'Road Pavement' discipline." />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <RuleIcon />
                </ListItemIcon>
                <ListItemText primary="Sub-Discipline Subscription" secondary="Must be subscribed to the relevant sub-discipline ('Traffic Safety' or 'Pavement Defect')." />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Email Preview Section */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Email Preview
        </Typography>
        <Card variant="outlined">
          <CardContent>
            {loadingTemplate ? (
              <CircularProgress />
            ) : (
              <Box dangerouslySetInnerHTML={{ __html: template }} />
            )}
          </CardContent>
        </Card>
      </Paper>
    </>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <EmailIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Daily Email Summary Flow
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage automated daily defect notification workflows
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Workflows" icon={<FlowIcon />} iconPosition="start" />
          <Tab label="Email Logs" icon={<HistoryIcon />} iconPosition="start" />
          <Tab label="Configuration" icon={<ScheduleIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && renderWorkflowsTab()}
      {activeTab === 1 && renderEmailLogsTab()}
      {activeTab === 2 && renderConfigurationTab()}

      {/* Add/Edit Workflow Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {editingWorkflow ? 'Edit Workflow' : 'Add New Workflow'}
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography variant="subtitle1">{step.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
                <StepContent>
                  {getStepContent(index)}
                  <Box sx={{ mt: 2 }}>
                    <Stack direction="row" spacing={2}>
                      {index === steps.length - 1 ? (
                        <Button
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={loading || !isStepValid(index)}
                          startIcon={<SaveIcon />}
                        >
                          {loading ? 'Saving...' : editingWorkflow ? 'Update Workflow' : 'Create Workflow'}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          disabled={!isStepValid(index)}
                        >
                          Next
                        </Button>
                      )}
                      {index > 0 && (
                        <Button onClick={handleBack}>
                          Back
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </DialogContent>
      </Dialog>

      {/* Log Detail Dialog */}
      <Dialog
        open={logDetailOpen}
        onClose={closeLogDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Email Log Details</Typography>
            <IconButton onClick={closeLogDetail}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  icon={selectedLog.status === 'sent' ? <CheckCircleIcon /> : <ErrorIcon />}
                  label={selectedLog.status === 'sent' ? 'Sent' : 'Failed'}
                  color={selectedLog.status === 'sent' ? 'success' : 'error'}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Sent At</Typography>
                <Typography>{formatDateTime(selectedLog.sent_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Subject</Typography>
                <Typography>{selectedLog.subject}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Workflow</Typography>
                <Typography>{selectedLog.workflow_name || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Defect Type</Typography>
                <Typography>{selectedLog.defect_type_name || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">TO Recipients ({(selectedLog.to_recipients || []).length})</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {(selectedLog.to_recipients || []).map((r, i) => (
                    <Chip key={i} label={r.email || r.full_name} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                  ))}
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">CC Recipients ({(selectedLog.cc_recipients || []).length})</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {(selectedLog.cc_recipients || []).length > 0 ? (
                    (selectedLog.cc_recipients || []).map((r, i) => (
                      <Chip key={i} label={r.email || r.full_name} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">None</Typography>
                  )}
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Defect IDs ({selectedLog.defect_count || 0})</Typography>
                <Typography>{(selectedLog.defect_ids || []).join(', ') || '-'}</Typography>
              </Box>
              {selectedLog.error_message && (
                <Box>
                  <Typography variant="subtitle2" color="error">Error Message</Typography>
                  <Alert severity="error">{selectedLog.error_message}</Alert>
                </Box>
              )}
              {selectedLog.smtp_message_id && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">SMTP Message ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedLog.smtp_message_id}</Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLogDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EmailFlowPage;
